import { useState, useEffect } from 'react';
import axios from 'axios';
import { AdminLayout } from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { ShoppingCart, Eye } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ORDER_STATUSES = ['pending', 'processing', 'shipped', 'delivered', 'cancelled'];

const formatDate = (dateStr) => {
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
  });
};

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/admin/orders`);
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (orderId, status) => {
    try {
      await axios.put(`${API}/admin/orders/${orderId}/status?status=${status}`);
      toast.success('Order status updated');
      fetchOrders();
    } catch (error) {
      toast.error('Error updating status');
    }
  };

  const toggleView = (order) => {
    setSelectedOrder(selectedOrder?.id === order.id ? null : order);
  };

  const renderOrderRow = (order) => (
    <tr key={order.id} className="border-b border-[#E5E7EB]">
      <td className="py-3 px-4 font-mono text-sm">{order.id.slice(0, 8)}...</td>
      <td className="py-3 px-4"><div><p className="font-medium text-[#1A1A1A]">{order.customer_name}</p><p className="text-xs text-[#6B7280]">{order.customer_email}</p></div></td>
      <td className="py-3 px-4 text-[#6B7280]">{order.items.length} item(s)</td>
      <td className="py-3 px-4 font-medium">${order.total.toFixed(2)}</td>
      <td className="py-3 px-4 text-sm text-[#6B7280]">{formatDate(order.created_at)}</td>
      <td className="py-3 px-4">
        <Select value={order.status} onValueChange={(v) => updateStatus(order.id, v)}>
          <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
          <SelectContent>{ORDER_STATUSES.map((s) => <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>)}</SelectContent>
        </Select>
      </td>
      <td className="py-3 px-4"><div className="flex justify-end"><Button variant="outline" size="icon" onClick={() => toggleView(order)}><Eye size={16} /></Button></div></td>
    </tr>
  );

  const renderOrderItem = (item, i) => (
    <div key={i} className="flex justify-between text-sm">
      <span>{item.product_name} x {item.quantity}</span>
      <span className="font-medium">${(item.price * item.quantity).toFixed(2)}</span>
    </div>
  );

  return (
    <AdminLayout title="Orders">
      <div data-testid="admin-orders">
        {loading ? (
          <div className="flex justify-center py-20"><div className="spinner" /></div>
        ) : orders.length === 0 ? (
          <div className="text-center py-20 bg-white">
            <ShoppingCart size={48} className="mx-auto text-[#E5E7EB] mb-4" />
            <h3 className="font-semibold text-[#1A1A1A] mb-2">No orders yet</h3>
            <p className="text-[#6B7280]">Orders will appear here once customers start purchasing</p>
          </div>
        ) : (
          <div className="bg-white shadow-sm overflow-x-auto">
            <table className="w-full" data-testid="orders-table">
              <thead>
                <tr className="border-b border-[#E5E7EB] bg-[#F9F7F2]">
                  <th className="text-left py-3 px-4 text-sm font-medium text-[#6B7280]">Order ID</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-[#6B7280]">Customer</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-[#6B7280]">Items</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-[#6B7280]">Total</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-[#6B7280]">Date</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-[#6B7280]">Status</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-[#6B7280]">Actions</th>
                </tr>
              </thead>
              <tbody>{orders.map(renderOrderRow)}</tbody>
            </table>

            {selectedOrder && (
              <div className="border-t border-[#E5E7EB] p-6 bg-[#F9F7F2]" data-testid="order-details">
                <h3 className="font-semibold text-[#1A1A1A] mb-4">Order Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-sm font-medium text-[#6B7280] mb-2">Shipping Address</h4>
                    <p className="text-[#1A1A1A]">{selectedOrder.customer_name}<br />{selectedOrder.shipping_address}<br />{selectedOrder.city}, {selectedOrder.state} {selectedOrder.zip_code}</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-[#6B7280] mb-2">Order Items</h4>
                    <div className="space-y-2">
                      {selectedOrder.items.map(renderOrderItem)}
                      <div className="border-t pt-2 mt-2">
                        <div className="flex justify-between font-medium"><span>Total</span><span>${selectedOrder.total.toFixed(2)}</span></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
